import { useLocation } from 'react-router-dom';
import { useEntranceAudio } from '@/lib/entranceAudio';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Bottom-left mini pill: Play/Pause + Mute + an expandable volume slider
// (reveals on hover on desktop) + a thin progress line.
// Renders only once the signature music has been started (via ENTER or Play).
// Hidden on the Home route ('/app'), which has the full expanded player instead.
export default function EntranceMusicControl() {
  const { pathname } = useLocation();
  const { hasStarted, playing, muted, desiredVolume, currentTime, duration, homeExpandedInView, togglePlay, toggleMute, setVolume } = useEntranceAudio();
  if (!hasStarted) return null;
  // Never cover auth / form screens (sign-in, register, password, verify, assessment)
  // — the pill otherwise sits over the sign-in card on mobile.
  if (/^\/(login|register|forgot-password|reset-password|verify-email|assessment)(\/|$)/.test(pathname)) return null;
  // On Home, the expanded player is the control while it's on-screen; once the
  // member scrolls past it, this slim pill takes over as the collapsed bar.
  const onHome = pathname === '/app' || pathname === '/app/';
  if (onHome && homeExpandedInView) return null;

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const vol = muted ? 0 : (desiredVolume ?? 0.5);

  return (
    <div
      data-testid="entrance-music-control"
      className="group fixed left-4 bottom-4 max-sm:bottom-24 z-[60] select-none"
    >
      <div className="relative flex items-center gap-1 rounded-full pl-1.5 pr-2.5 py-1.5 overflow-hidden
        bg-navy/90 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.4)] border border-gold/30">
        <button
          type="button"
          data-testid="music-play-toggle"
          onClick={togglePlay}
          aria-label={playing ? 'Pause music' : 'Play music'}
          className="w-8 h-8 rounded-full flex items-center justify-center text-navy bg-gold hover:brightness-105 transition-smooth"
        >
          {playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" />}
        </button>
        <button
          type="button"
          data-testid="music-mute-toggle"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute music' : 'Mute music'}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-smooth"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>

        {/* Expandable volume slider — reveals on hover (desktop) */}
        <div className="flex items-center overflow-hidden transition-all duration-300 ease-out
          w-0 opacity-0 group-hover:w-24 group-hover:opacity-100 group-focus-within:w-24 group-focus-within:opacity-100">
          <input
            type="range"
            data-testid="music-volume-slider"
            min="0" max="1" step="0.01"
            value={vol}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            aria-label="Music volume"
            className="w-20 h-1 cursor-pointer accent-[#C5A059] bg-white/20 rounded-full appearance-none"
          />
        </div>

        <span className="text-[10px] font-semibold tracking-wide text-white/45 pr-0.5 hidden sm:inline group-hover:hidden">
          Brotherhood+
        </span>
        {/* progress line */}
        <span className="absolute left-0 bottom-0 h-[2px] bg-gold/70 transition-[width] duration-300"
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
